// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import classNames from 'classnames'
import groupBy from 'lodash/groupBy'
import isEqual from 'lodash/isEqual'
import mapValues from 'lodash/mapValues'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import React, { useMemo, useState, useCallback } from 'react'
import type { JSX } from 'react'
import styled from 'styled-components'

import DateRange from 'lib-common/date-range'
import type { ErrorKey } from 'lib-common/form-validation'
import type {
  Attendance,
  EmployeeAttendance,
  ExternalAttendance,
  PlannedStaffAttendance,
  StaffAttendanceUpsert,
  ExternalAttendanceUpsert,
  StaffAttendanceType
} from 'lib-common/generated/api-types/attendance'
import type { DaycareGroup } from 'lib-common/generated/api-types/daycare'
import type { OperationalDay } from 'lib-common/generated/api-types/reservations'
import type {
  DaycareId,
  EmployeeId,
  GroupId,
  StaffAttendanceExternalId,
  StaffAttendanceRealtimeId
} from 'lib-common/generated/api-types/shared'
import type { EvakaUser } from 'lib-common/generated/api-types/user'
import HelsinkiDateTime from 'lib-common/helsinki-date-time'
import LocalDate from 'lib-common/local-date'
import LocalTime from 'lib-common/local-time'
import { formatPersonName } from 'lib-common/names'
import { useMutationResult } from 'lib-common/query'
import { presentInGroup } from 'lib-common/staff-attendance'
import type { UUID } from 'lib-common/types'
import RoundIcon from 'lib-components/atoms/RoundIcon'
import Tooltip from 'lib-components/atoms/Tooltip'
import AddButton from 'lib-components/atoms/buttons/AddButton'
import { IconOnlyButton } from 'lib-components/atoms/buttons/IconOnlyButton'
import { Table, Tbody } from 'lib-components/layout/Table'
import { FixedSpaceRow } from 'lib-components/layout/flex-helpers'
import { AlertBox } from 'lib-components/molecules/MessageBoxes'
import { fontWeights, Italic, P } from 'lib-components/typography'
import type { BaseProps } from 'lib-components/utils'
import { defaultMargins } from 'lib-components/white-space'
import { colors } from 'lib-customizations/common'
import { faCircleEllipsisVertical, fasExclamationTriangle } from 'lib-icons'

import { useTranslation } from '../../../state/i18n'
import {
  upsertExternalAttendancesMutation,
  upsertStaffAttendancesMutation
} from '../queries'

import type {
  EditedAttendance,
  ModalAttendance,
  ModalPlannedAttendance,
  ValidationError
} from './StaffAttendanceDetailsModal'
import StaffAttendanceDetailsModal from './StaffAttendanceDetailsModal'
import StaffAttendanceExternalPersonModal from './StaffAttendanceExternalPersonModal'
import {
  AttendanceTableHeader,
  DayTd,
  DayTr,
  NameTd,
  NameWrapper
} from './attendance-elements'

type GroupFilter = (ids: UUID[]) => boolean

interface Props {
  unitId: DaycareId
  operationalDays: OperationalDay[]
  staffAttendances: EmployeeAttendance[]
  externalAttendances: ExternalAttendance[]
  groups: DaycareGroup[]
  groupFilter: GroupFilter | null
  defaultGroup: GroupId | null
}

type DetailsModalTarget =
  | { type: 'employee'; employeeId: EmployeeId; hasOccupancyEffect: boolean }
  | { type: 'external'; name: string }

interface DetailsModalConfig {
  date: LocalDate
  name: string
  attendances: ModalAttendance[]
  plannedAttendances?: ModalPlannedAttendance[]
  target: DetailsModalTarget
}

export default React.memo(function StaffAttendanceTable({
  unitId,
  staffAttendances,
  externalAttendances,
  operationalDays,
  groups,
  groupFilter,
  defaultGroup
}: Props) {
  const { i18n } = useTranslation()
  const [detailsModalConfig, setDetailsModalConfig] =
    useState<DetailsModalConfig>()
  const closeDetailsModal = useCallback(
    () => setDetailsModalConfig(undefined),
    []
  )

  const [showExternalPersonModal, setShowExternalPersonModal] =
    useState<boolean>(false)
  const toggleAddPersonModal = useCallback(
    () => setShowExternalPersonModal((prev) => !prev),
    []
  )

  const staffRows: StaffRow[] = useMemo(
    () => getStaffRows(staffAttendances, groupFilter),
    [staffAttendances, groupFilter]
  )

  const externalRowsGroupedByName = useMemo(
    () => getExternalRows(externalAttendances, groupFilter),
    [externalAttendances, groupFilter]
  )

  const personCountSumsPerDate = useMemo(
    () =>
      computePersonCountSums(staffRows, externalRowsGroupedByName, groupFilter),
    [staffRows, externalRowsGroupedByName, groupFilter]
  )

  const automaticDepartureCount = useMemo(
    () =>
      countAutomaticDepartures(
        staffRows,
        externalRowsGroupedByName,
        groupFilter
      ),
    [staffRows, externalRowsGroupedByName, groupFilter]
  )

  const modalData = useMemo(
    () =>
      detailsModalConfig
        ? computeModalAttendances(
            detailsModalConfig,
            staffAttendances,
            externalAttendances
          )
        : undefined,
    [detailsModalConfig, externalAttendances, staffAttendances]
  )

  return (
    <>
      {automaticDepartureCount > 0 && (
        <AlertBox
          message={i18n.unit.staffAttendance.departedAutomaticallyBanner(
            automaticDepartureCount
          )}
          data-qa="automatic-departures-banner"
          thin
          noMargin
        />
      )}
      <Table data-qa="staff-attendances-table">
        <AttendanceTableHeader
          operationalDays={operationalDays}
          nameColumnLabel={i18n.unit.staffAttendance.staffName}
        />
        <Tbody>
          {staffRows.map((row, index) => (
            <AttendanceRow
              key={`${row.employeeId}-${index}`}
              rowIndex={index}
              isPositiveOccupancyCoefficient={
                row.currentOccupancyCoefficient > 0
              }
              name={row.name}
              employeeId={row.employeeId}
              operationalDays={operationalDays}
              attendances={row.attendances}
              plannedAttendances={row.plannedAttendances}
              groupFilter={groupFilter}
              openDetails={(date: LocalDate) => {
                setDetailsModalConfig({
                  date,
                  name: row.name,
                  attendances: row.attendances.flatMap((a) =>
                    a.type !== null
                      ? ({
                          id: a.id,
                          groupId: a.groupId,
                          type: a.type,
                          arrived: a.arrived,
                          departed: a.departed,
                          departedAutomatically: a.departedAutomatically,
                          occupancyCoefficient: a.occupancyCoefficient
                        } satisfies ModalAttendance)
                      : []
                  ),
                  plannedAttendances: row.plannedAttendances,
                  target: {
                    type: 'employee',
                    employeeId: row.employeeId,
                    hasOccupancyEffect: row.currentOccupancyCoefficient > 0
                  }
                })
              }}
              allowedToEdit={row.allowedToEdit}
            />
          ))}
          {externalRowsGroupedByName.map((row, index) => (
            <AttendanceRow
              key={`${row.name}-${index}`}
              rowIndex={staffRows.length + index}
              isPositiveOccupancyCoefficient={
                row.attendances[0].occupancyCoefficient > 0
              }
              name={row.name}
              operationalDays={operationalDays}
              attendances={row.attendances.map((attendance) => ({
                ...attendance,
                arrivedAddedAt: null,
                arrivedAddedBy: null,
                arrivedModifiedAt: null,
                arrivedModifiedBy: null,
                departedAddedAt: null,
                departedAddedBy: null,
                departedModifiedAt: null,
                departedModifiedBy: null
              }))}
              groupFilter={groupFilter}
              openDetails={(date: LocalDate) => {
                setDetailsModalConfig({
                  date,
                  name: row.name,
                  attendances: row.attendances,
                  target: { type: 'external', name: row.name }
                })
              }}
              allowedToEdit={row.allowedToEdit}
            />
          ))}
        </Tbody>
        <tfoot>
          <BottomSumTr>
            <BottomSumTd>{i18n.unit.staffAttendance.personCount}</BottomSumTd>
            {operationalDays.map(({ date }) => (
              <BottomSumTd
                $centered
                key={date.toString()}
                data-qa="person-count-sum"
              >
                {personCountSumsPerDate[date.toString()] ?? '–'}{' '}
                {i18n.unit.staffAttendance.personCountAbbr}
              </BottomSumTd>
            ))}
          </BottomSumTr>
        </tfoot>
      </Table>
      {groups.length > 0 ? (
        <AddButton
          text={i18n.unit.staffAttendance.addPerson}
          aria-label={i18n.unit.staffAttendance.addPerson}
          data-qa="add-person-button"
          onClick={toggleAddPersonModal}
        />
      ) : null}
      {detailsModalConfig && modalData ? (
        <StaffAttendanceModal
          detailsModalConfig={detailsModalConfig}
          modalData={modalData}
          unitId={unitId}
          groups={groups}
          defaultGroupId={defaultGroup}
          onClose={closeDetailsModal}
        />
      ) : null}
      {showExternalPersonModal && (
        <StaffAttendanceExternalPersonModal
          onClose={toggleAddPersonModal}
          onSave={toggleAddPersonModal}
          unitId={unitId}
          groups={groups}
          defaultGroupId={defaultGroup ?? groups[0].id}
        />
      )}
    </>
  )
})

const StaffAttendanceModal = React.memo(function StaffAttendanceModal({
  detailsModalConfig,
  modalData,
  unitId,
  groups,
  defaultGroupId,
  onClose
}: {
  detailsModalConfig: DetailsModalConfig
  modalData: {
    attendances: ModalAttendance[]
    plannedAttendances: ModalPlannedAttendance[]
  }
  unitId: DaycareId
  groups: DaycareGroup[]
  defaultGroupId: GroupId | null
  onClose: () => void
}) {
  const { mutateAsync: upsertStaffAttendances } = useMutationResult(
    upsertStaffAttendancesMutation
  )
  const { mutateAsync: upsertExternalAttendances } = useMutationResult(
    upsertExternalAttendancesMutation
  )
  const { target, date } = detailsModalConfig
  const onSaveStaff = useCallback(
    (entries: StaffAttendanceUpsert[]) =>
      target.type === 'employee'
        ? upsertStaffAttendances({
            body: {
              unitId,
              employeeId: target.employeeId,
              date,
              entries
            }
          })
        : undefined,
    [date, target, unitId, upsertStaffAttendances]
  )
  const onSaveExternal = useCallback(
    (entries: ExternalAttendanceUpsert[]) =>
      target.type === 'external'
        ? upsertExternalAttendances({
            body: {
              unitId,
              name: target.name,
              date,
              entries
            }
          })
        : undefined,
    [date, target, unitId, upsertExternalAttendances]
  )
  return target.type === 'employee' ? (
    <StaffAttendanceDetailsModal
      isExternal={false}
      onSave={onSaveStaff}
      staffOccupancyEffectDefault={target.hasOccupancyEffect}
      validate={staffAttendanceValidator(detailsModalConfig)}
      name={detailsModalConfig.name}
      employeeId={target.employeeId}
      date={detailsModalConfig.date}
      attendances={modalData.attendances}
      plannedAttendances={modalData.plannedAttendances}
      groups={groups}
      defaultGroupId={defaultGroupId}
      onClose={onClose}
      onSuccess={onClose}
      unitId={unitId}
    />
  ) : (
    <StaffAttendanceDetailsModal
      isExternal={true}
      onSave={onSaveExternal}
      staffOccupancyEffectDefault={
        detailsModalConfig.attendances[0] === undefined ||
        detailsModalConfig.attendances[0].occupancyCoefficient > 0
      }
      validate={externalAttendanceValidator(detailsModalConfig)}
      name={detailsModalConfig.name}
      date={detailsModalConfig.date}
      attendances={modalData.attendances}
      plannedAttendances={modalData.plannedAttendances}
      groups={groups}
      defaultGroupId={defaultGroupId}
      onClose={onClose}
      onSuccess={onClose}
      unitId={unitId}
    />
  )
})

interface StaffRow {
  employeeId: EmployeeId
  name: string
  attendances: Attendance[]
  plannedAttendances: PlannedStaffAttendance[]
  employeeGroups: UUID[]
  currentOccupancyCoefficient: number
  allowedToEdit: boolean
}

function getStaffRows(
  staffAttendances: EmployeeAttendance[],
  groupFilter: GroupFilter | null
): StaffRow[] {
  return sortBy(
    staffAttendances
      .map((entry): StaffRow => ({
        employeeId: entry.employeeId,
        name: formatPersonName(entry, 'Last FirstFirst'),
        attendances: sortBy(
          entry.attendances.filter(
            (a) => !groupFilter || (a.groupId && groupFilter([a.groupId]))
          ),
          ({ departed }) => departed?.timestamp ?? Infinity
        ),
        plannedAttendances: entry.plannedAttendances,
        employeeGroups: entry.groups,
        currentOccupancyCoefficient: entry.currentOccupancyCoefficient,
        allowedToEdit: entry.allowedToEdit
      }))
      .filter(
        (row) =>
          !groupFilter ||
          groupFilter(row.employeeGroups) ||
          row.attendances.length > 0
      ),
    (attendance) => attendance.name
  )
}

interface ExternalRow {
  name: string
  attendances: ExternalAttendance[]
  allowedToEdit: boolean
}

function getExternalRows(
  externalAttendances: ExternalAttendance[],
  groupFilter: GroupFilter | null
): ExternalRow[] {
  return sortBy(
    Object.entries(groupBy(externalAttendances, (a) => a.name))
      .map(([name, attendances]): ExternalRow => ({
        name,
        attendances: sortBy(
          attendances.filter((a) => !groupFilter || groupFilter([a.groupId])),
          ({ departed }) => departed?.timestamp ?? Infinity
        ),
        allowedToEdit: true
      }))
      .filter((row) => row.attendances.length > 0),
    (attendance) => attendance.name
  )
}

function computePersonCountSums(
  staffRows: StaffRow[],
  externalRows: ExternalRow[],
  groupFilter: GroupFilter | null
): Record<string, number | undefined> {
  const employeeAttendanceDates = staffRows
    .flatMap(({ attendances, employeeId }) =>
      getUniqueAttendanceDates(attendances, groupFilter).map(
        (date): { date: LocalDate; employeeKey: string } => ({
          date,
          employeeKey: employeeId
        })
      )
    )
    .concat(
      externalRows.flatMap((row) =>
        getUniqueAttendanceDates(row.attendances, groupFilter).map((date) => ({
          date,
          employeeKey: `external-${row.name}`
        }))
      )
    )
  return mapValues(
    groupBy(employeeAttendanceDates, ({ date }) => date.toString()),
    (rows) => uniqBy(rows, ({ employeeKey }) => employeeKey).length
  )
}

function countAutomaticDepartures(
  staffRows: StaffRow[],
  externalRows: ExternalRow[],
  groupFilter: GroupFilter | null
): number {
  const countFromAttendances = (
    attendances: readonly {
      departedAutomatically: boolean
      type: StaffAttendanceType | null
    }[]
  ) =>
    attendances.filter(
      (a) =>
        a.departedAutomatically &&
        (!groupFilter || (a.type !== null && presentInGroup(a.type)))
    ).length

  const staffCount = staffRows.reduce(
    (sum, row) => sum + countFromAttendances(row.attendances),
    0
  )
  const externalCount = externalRows.reduce(
    (sum, row) => sum + countFromAttendances(row.attendances),
    0
  )
  return staffCount + externalCount
}

function getUniqueAttendanceDates(
  attendances: Attendance[] | ExternalAttendance[],
  groupFilter: GroupFilter | null
): LocalDate[] {
  return uniqBy(
    attendances
      .filter(
        ({ type, groupId }) =>
          !groupFilter ||
          (groupId &&
            groupFilter([groupId]) &&
            type != null &&
            presentInGroup(type))
      )
      // TODO: What if arrived and departed are > 1 day apart?
      .flatMap(({ departed, arrived }) =>
        [arrived.toLocalDate()].concat(departed?.toLocalDate() ?? [])
      ),
    (localDate) => localDate.date
  )
}

const BottomSumTr = styled.tr`
  background-color: ${(p) => p.theme.colors.grayscale.g4};
  font-weight: ${fontWeights.semibold};
`

const BottomSumTd = styled.td<{ $centered?: boolean }>`
  padding: ${defaultMargins.xs} ${defaultMargins.s};
  text-align: ${(p) => (p.$centered ? 'center' : 'left')};
`

interface AttendanceRowAttendance {
  arrived: HelsinkiDateTime
  departed: HelsinkiDateTime | null
  groupId: GroupId | null
  departedAutomatically: boolean
  type: StaffAttendanceType | null
  arrivedAddedAt: HelsinkiDateTime | null
  arrivedAddedBy: EvakaUser | null
  arrivedModifiedAt: HelsinkiDateTime | null
  arrivedModifiedBy: EvakaUser | null
  departedAddedAt: HelsinkiDateTime | null
  departedAddedBy: EvakaUser | null
  departedModifiedAt: HelsinkiDateTime | null
  departedModifiedBy: EvakaUser | null
}

interface AttendanceRowProps extends BaseProps {
  rowIndex: number
  isPositiveOccupancyCoefficient: boolean
  name: string
  employeeId?: string
  operationalDays: OperationalDay[]
  attendances: AttendanceRowAttendance[]
  plannedAttendances?: PlannedStaffAttendance[]
  groupFilter: GroupFilter | null
  openDetails: (date: LocalDate) => void
  allowedToEdit: boolean
}

const AttendanceRow = React.memo(function AttendanceRow({
  rowIndex,
  isPositiveOccupancyCoefficient,
  name,
  employeeId,
  operationalDays,
  attendances,
  plannedAttendances,
  groupFilter,
  openDetails,
  allowedToEdit
}: AttendanceRowProps) {
  const { i18n } = useTranslation()
  const today = LocalDate.todayInHelsinkiTz()
  const [hovered, setHovered] = useState<LocalDate | null>(null)
  const isHovered = (date: LocalDate) =>
    hovered !== null && hovered.isEqual(date)

  const timeTooltipText = ({
    addedAt,
    addedBy,
    modifiedAt,
    modifiedBy
  }: {
    addedAt: HelsinkiDateTime | null
    addedBy: EvakaUser | null
    modifiedAt: HelsinkiDateTime | null
    modifiedBy: EvakaUser | null
  }) => {
    if (
      addedAt === null &&
      addedBy === null &&
      modifiedAt === null &&
      modifiedBy === null
    ) {
      return undefined
    }
    return (
      <>
        {addedAt !== null && addedBy !== null && (
          <P>
            {i18n.unit.staffAttendance.addedAt} {addedAt.format()},{' '}
            <Italic>{addedBy.name}</Italic>
          </P>
        )}
        {modifiedAt !== null &&
          modifiedBy !== null &&
          (addedAt === null ||
            !addedAt.isEqual(modifiedAt) ||
            !isEqual(addedBy, modifiedBy)) && (
            <P>
              {i18n.unit.staffAttendance.modifiedAt} {modifiedAt.format()},{' '}
              <Italic>{modifiedBy.name}</Italic>
            </P>
          )}
      </>
    )
  }
  const departureTooltipText = (attendance: AttendanceRowAttendance) => {
    const timeTooltip = timeTooltipText({
      addedAt: attendance.departedAddedAt,
      addedBy: attendance.departedAddedBy,
      modifiedAt: attendance.departedModifiedAt,
      modifiedBy: attendance.departedModifiedBy
    })

    return timeTooltip !== undefined || attendance.departedAutomatically ? (
      <div>
        {timeTooltip !== undefined && timeTooltip}
        {attendance.departedAutomatically && (
          <P>{i18n.unit.staffAttendance.departedAutomatically}</P>
        )}
      </div>
    ) : undefined
  }

  return (
    <DayTr data-qa={`attendance-row-${rowIndex}`}>
      <NameTd $partialRow={false} $rowIndex={rowIndex}>
        <FixedSpaceRow $spacing="xs">
          <Tooltip
            tooltip={
              isPositiveOccupancyCoefficient
                ? i18n.unit.attendanceReservations.affectsOccupancy
                : i18n.unit.attendanceReservations.doesNotAffectOccupancy
            }
            position="bottom"
            width="large"
            data-qa="coeff-tooltip"
          >
            <RoundIcon
              content="K"
              active={isPositiveOccupancyCoefficient}
              color={colors.accents.a3emerald}
              size="s"
              data-qa={
                isPositiveOccupancyCoefficient
                  ? 'icon-occupancy-coefficient-pos'
                  : 'icon-occupancy-coefficient'
              }
            />
          </Tooltip>
          <NameWrapper data-qa="staff-attendance-name">{name}</NameWrapper>
        </FixedSpaceRow>
      </NameTd>
      {operationalDays
        .map(({ date }) => {
          const matchingAttendances = getAttendancesForGroupAndDate(
            attendances,
            groupFilter,
            date
          )
          const plannedAttendancesForToday = getPlannedAttendancesForDate(
            plannedAttendances ?? [],
            date
          )
          return {
            date,
            plannedAttendancesForToday,
            attendancesForToday: matchingAttendances,
            someAttendanceDepartedAutomatically: matchingAttendances.some(
              (a) => a.departedAutomatically
            ),
            allowDetailsModal: date.isEqualOrBefore(today) && allowedToEdit
          }
        })
        .map(
          ({
            date,
            attendancesForToday,
            plannedAttendancesForToday,
            someAttendanceDepartedAutomatically,
            allowDetailsModal
          }) => (
            <DayTd
              key={date.formatIso()}
              className={classNames({ 'is-today': date.isToday() })}
              $partialRow={false}
              $rowIndex={rowIndex}
              onMouseEnter={() => setHovered(date)}
              onMouseLeave={() => setHovered(null)}
              data-qa={`day-cell-${employeeId ?? ''}-${date.formatIso()}`}
            >
              <DayCell data-qa={`attendance-${date.formatIso()}-${rowIndex}`}>
                <PlannedAttendanceTimes data-qa="planned-attendance-day">
                  {plannedAttendancesForToday.length > 0 ? (
                    plannedAttendancesForToday.map((plannedAttendance, i) => (
                      <AttendanceCell key={i}>
                        <AttendanceTime data-qa="planned-attendance-start">
                          {renderTime(plannedAttendance.start, date)}
                        </AttendanceTime>
                        <AttendanceTime data-qa="planned-attendance-end">
                          {renderTime(plannedAttendance.end, date)}
                        </AttendanceTime>
                        <DetailsCell />
                      </AttendanceCell>
                    ))
                  ) : (
                    <AttendanceCell>
                      <AttendanceTime>{renderTime(null, date)}</AttendanceTime>
                      <AttendanceTime>{renderTime(null, date)}</AttendanceTime>
                      <DetailsCell />
                    </AttendanceCell>
                  )}
                </PlannedAttendanceTimes>
                <AttendanceTimes data-qa="attendance-day">
                  {attendancesForToday.length > 0 ? (
                    attendancesForToday.map((attendance, i) => {
                      const isLast = i === attendancesForToday.length - 1
                      return (
                        <AttendanceCell
                          key={i}
                          $automaticDeparture={attendance.departedAutomatically}
                        >
                          <AttendanceTime data-qa="arrival-time">
                            <Tooltip
                              tooltip={timeTooltipText({
                                addedAt: attendance.arrivedAddedAt,
                                addedBy: attendance.arrivedAddedBy,
                                modifiedAt: attendance.arrivedModifiedAt,
                                modifiedBy: attendance.arrivedModifiedBy
                              })}
                              data-qa="arrival-time-tooltip"
                            >
                              {renderTime(attendance.arrived, date)}
                            </Tooltip>
                          </AttendanceTime>
                          <AttendanceTime data-qa="departure-time">
                            <Tooltip
                              tooltip={departureTooltipText(attendance)}
                              data-qa="departure-time-tooltip"
                            >
                              {renderTime(
                                attendance.departed,
                                date,
                                attendance.departedAutomatically
                              )}
                            </Tooltip>
                          </AttendanceTime>
                          {isLast ? (
                            <DetailsToggle
                              onOpen={() => openDetails(date)}
                              hovered={isHovered(date)}
                              allowDetailsModal={allowDetailsModal}
                              departedAutomatically={
                                someAttendanceDepartedAutomatically
                              }
                            />
                          ) : (
                            <DetailsCell />
                          )}
                        </AttendanceCell>
                      )
                    })
                  ) : (
                    <AttendanceCell>
                      <AttendanceTime>{renderTime(null, date)}</AttendanceTime>
                      <AttendanceTime>{renderTime(null, date)}</AttendanceTime>
                      <DetailsToggle
                        onOpen={() => openDetails(date)}
                        hovered={isHovered(date)}
                        allowDetailsModal={allowDetailsModal}
                        departedAutomatically={false}
                      />
                    </AttendanceCell>
                  )}
                </AttendanceTimes>
              </DayCell>
            </DayTd>
          )
        )}
    </DayTr>
  )
})

interface DetailsToggleProps {
  onOpen: () => void
  hovered: boolean
  allowDetailsModal: boolean
  departedAutomatically: boolean
  className?: string
}

const DetailsToggle = React.memo(function DetailsToggle({
  onOpen,
  hovered,
  allowDetailsModal,
  departedAutomatically
}: DetailsToggleProps) {
  const { i18n } = useTranslation()
  const showWarning = departedAutomatically && !hovered

  return (
    <DetailsCell
      data-qa={
        departedAutomatically ? 'departed-automatically-icon' : undefined
      }
    >
      {allowDetailsModal || showWarning ? (
        <OpenDetailsButton
          $dimmed={!hovered && !departedAutomatically}
          icon={showWarning ? fasExclamationTriangle : faCircleEllipsisVertical}
          color={showWarning ? 'warning' : 'default'}
          bubble={departedAutomatically && hovered ? 'warning' : undefined}
          onClick={allowDetailsModal ? onOpen : undefined}
          data-qa="open-details"
          aria-label={i18n.common.open}
        />
      ) : null}
    </DetailsCell>
  )
})

const DetailsCell = styled.div`
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${defaultMargins.xxs};
`

const OpenDetailsButton = styled(IconOnlyButton)<{ $dimmed: boolean }>`
  opacity: ${(p) => (p.$dimmed ? 0 : 1)};
`

function getAttendancesForGroupAndDate(
  attendances: AttendanceRowAttendance[],
  groupFilter: GroupFilter | null,
  date: LocalDate
): AttendanceRowAttendance[] {
  return attendances
    .filter(
      (a) =>
        a.arrived.toLocalDate().isEqual(date) ||
        (a.departed &&
          new DateRange(
            a.arrived.toLocalDate(),
            a.departed.toLocalDate()
          ).includes(date))
    )
    .filter(
      ({ groupId, type }) =>
        !groupFilter ||
        (groupId &&
          groupFilter([groupId]) &&
          type !== null &&
          presentInGroup(type))
    )
}

function getPlannedAttendancesForDate(
  plannedAttendances: PlannedStaffAttendance[],
  date: LocalDate
): PlannedStaffAttendance[] {
  return (plannedAttendances ?? []).filter(
    (plannedAttendance) =>
      plannedAttendance.start.toLocalDate().isEqual(date) ||
      plannedAttendance.end.toLocalDate().isEqual(date)
  )
}

function renderTime(
  timestamp: HelsinkiDateTime | null,
  today: LocalDate,
  automaticDeparture = false
): JSX.Element | string {
  if (timestamp && !timestamp.toLocalDate().isEqual(today)) return '→'
  if (timestamp === null) return <NoTime>–</NoTime>
  const formatted = timestamp.toLocalTime().format()
  return automaticDeparture ? <Warning>{formatted}</Warning> : formatted
}

const NoTime = styled.span`
  color: ${(p) => p.theme.colors.grayscale.g35};
`

const Warning = styled.span`
  color: ${(p) => p.theme.colors.accents.a2orangeDark};
`

function computeModalAttendances(
  detailsModalConfig: DetailsModalConfig,
  staffAttendances: EmployeeAttendance[],
  externalAttendances: ExternalAttendance[]
): {
  attendances: ModalAttendance[]
  plannedAttendances: ModalPlannedAttendance[]
} {
  const startOfDay = HelsinkiDateTime.fromLocal(
    detailsModalConfig.date,
    LocalTime.of(0, 0)
  )
  const endOfDay = HelsinkiDateTime.fromLocal(
    detailsModalConfig.date.addDays(1),
    LocalTime.of(0, 0)
  )
  if (detailsModalConfig.target.type === 'employee') {
    const employeeId = detailsModalConfig.target.employeeId
    const combined: EmployeeAttendance[] = staffAttendances.filter(
      (attendance) => attendance.employeeId === employeeId
    )
    const attendances: ModalAttendance[] = uniqBy(
      combined
        .flatMap((employee) => employee.attendances)
        .flatMap((attendance) =>
          attendance.type !== null &&
          attendance.arrived <= endOfDay &&
          (attendance.departed === null || startOfDay <= attendance.departed)
            ? ({
                id: attendance.id,
                groupId: attendance.groupId,
                type: attendance.type,
                arrived: attendance.arrived,
                departed: attendance.departed,
                departedAutomatically: attendance.departedAutomatically,
                occupancyCoefficient: attendance.occupancyCoefficient
              } satisfies ModalAttendance)
            : []
        ),
      (attendance) => attendance.id
    )
    const plannedAttendances: ModalPlannedAttendance[] = uniq(
      combined.flatMap((employee) => employee.plannedAttendances)
    )
      .filter(({ start, end }) => start <= endOfDay && startOfDay <= end)
      .map(({ start, end, description }) => ({ start, end, description }))
    return { attendances, plannedAttendances }
  } else {
    const name = detailsModalConfig.target.name
    const attendances: ModalAttendance[] = externalAttendances.filter(
      (attendance) =>
        attendance.name === name &&
        attendance.arrived <= endOfDay &&
        (attendance.departed === null || startOfDay <= attendance.departed)
    )
    return { attendances, plannedAttendances: [] }
  }
}

type ValidatorConfig = Pick<DetailsModalConfig, 'date' | 'attendances'>

const staffAttendanceValidator =
  (config: ValidatorConfig) =>
  (
    state: EditedAttendance[]
  ): [undefined | StaffAttendanceUpsert[], ValidationError[]] => {
    const body: (StaffAttendanceUpsert | undefined)[] = []
    const errors: ValidationError[] = []

    const continuationAttendance = config.attendances.find(
      (a) =>
        a.arrived.toLocalDate().isBefore(config.date) &&
        (!a.departed || a.departed.toLocalDate().isEqualOrAfter(config.date))
    )

    for (let i = 0; i < state.length; i++) {
      const item = state[i]
      const isLastAttendance = i === state.length - 1

      const previousDeparted =
        i === 0 ? continuationAttendance?.departed : body[i - 1]?.departed
      const [arrived, arrivedError] = validateArrived(
        config,
        item,
        previousDeparted
      )
      const [departed, departedError] = validateDeparted(
        config,
        item,
        isLastAttendance,
        arrived
      )
      const [groupId, groupIdError] = validateGroupId(item)

      if (
        arrived !== undefined &&
        departed !== undefined &&
        groupId !== undefined
      ) {
        body[i] = {
          id: item.id as StaffAttendanceRealtimeId | null,
          type: item.type,
          arrived,
          departed,
          groupId,
          hasStaffOccupancyEffect: item.hasStaffOccupancyEffect
        }
      }

      errors[i] = {
        arrived: arrivedError,
        departed: departedError,
        groupId: groupIdError
      }
    }

    const a = body.filter((item): item is StaffAttendanceUpsert => !!item)
    if (a.length === state.length) {
      return [a, errors]
    }

    return [undefined, errors]
  }

const externalAttendanceValidator =
  (config: ValidatorConfig) =>
  (
    state: EditedAttendance[]
  ): [undefined | ExternalAttendanceUpsert[], ValidationError[]] => {
    const body: (ExternalAttendanceUpsert | undefined)[] = []
    const errors: ValidationError[] = []

    const continuationAttendance = config.attendances.find(
      (a) =>
        a.arrived.toLocalDate().isBefore(config.date) &&
        (!a.departed || a.departed.toLocalDate().isEqualOrAfter(config.date))
    )

    for (let i = 0; i < state.length; i++) {
      const item = state[i]
      const isLastAttendance = i === state.length - 1

      const previousDeparted =
        i === 0 ? continuationAttendance?.departed : body[i - 1]?.departed
      const [arrived, arrivedError] = validateArrived(
        config,
        item,
        previousDeparted
      )
      const [departed, departedError] = validateDeparted(
        config,
        item,
        isLastAttendance,
        arrived
      )
      const [groupId, groupIdError] = item.groupId
        ? [item.groupId, undefined]
        : [undefined, 'required' as const]

      if (
        arrived !== undefined &&
        departed !== undefined &&
        groupId !== undefined
      ) {
        body[i] = {
          ...item,
          id: item.id as StaffAttendanceExternalId | null,
          arrived,
          departed,
          groupId
        }
      }

      errors[i] = {
        arrived: arrivedError,
        departed: departedError,
        groupId: groupIdError
      }
    }

    const a = body.filter((item): item is ExternalAttendanceUpsert => !!item)
    if (a.length === state.length) {
      return [a, errors]
    }

    return [undefined, errors]
  }

const validateArrived = (
  config: ValidatorConfig,
  item: EditedAttendance,
  previousDeparted: HelsinkiDateTime | null | undefined
): [undefined, ErrorKey] | [HelsinkiDateTime, undefined] => {
  const parsedArrived = item.arrived
    ? LocalTime.tryParse(item.arrived)
    : undefined

  if (
    config.date.isToday() &&
    parsedArrived &&
    parsedArrived.isAfter(LocalTime.nowInHelsinkiTz())
  ) {
    return [undefined, 'generic']
  }

  if (!item.arrived) {
    return [undefined, 'required']
  }

  if (!parsedArrived) {
    return [undefined, 'timeFormat']
  }

  const arrived = HelsinkiDateTime.fromLocal(config.date, parsedArrived)
  if (previousDeparted && arrived.isBefore(previousDeparted)) {
    return [undefined, 'timeFormat']
  }

  return [arrived, undefined]
}

const validateDeparted = (
  config: ValidatorConfig,
  item: EditedAttendance,
  isLastAttendance: boolean,
  arrived: HelsinkiDateTime | undefined
): [undefined, ErrorKey] | [HelsinkiDateTime | null, undefined] => {
  const parsedDeparted = item.departed
    ? LocalTime.tryParse(item.departed)
    : undefined

  if (config.date.isToday() && parsedDeparted) {
    if (parsedDeparted.isAfter(LocalTime.nowInHelsinkiTz())) {
      return [undefined, 'generic']
    }
    if (
      arrived &&
      !HelsinkiDateTime.fromLocal(config.date, parsedDeparted).isAfter(arrived)
    ) {
      return [undefined, 'generic']
    }
  }

  if (!item.departed && isLastAttendance && config.date.isToday()) {
    return [null, undefined]
  }

  if (!item.departed) {
    return [undefined, 'required']
  }

  if (!parsedDeparted) {
    return [undefined, 'timeFormat']
  }

  const departed = HelsinkiDateTime.fromLocal(config.date, parsedDeparted)
  if (arrived && arrived.isEqual(departed)) {
    return [undefined, 'timeFormat']
  }

  if (arrived && departed.isBefore(arrived)) {
    if (isLastAttendance) {
      return [
        HelsinkiDateTime.fromLocal(
          departed.toLocalDate().addDays(1),
          departed.toLocalTime()
        ),
        undefined
      ]
    }
    return [undefined, 'timeFormat']
  }

  return [departed, undefined]
}

const validateGroupId = (
  item: EditedAttendance
): [undefined, ErrorKey] | [GroupId | null, undefined] => {
  if (presentInGroup(item.type) && item.groupId === null) {
    return [undefined, 'required']
  }

  return [item.groupId, undefined]
}

const DayCell = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`

const AttendanceTimes = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: ${colors.grayscale.g4};
  width: 100%;
`

const PlannedAttendanceTimes = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const AttendanceCell = styled.div<{ $automaticDeparture?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-evenly;
  padding: ${defaultMargins.xs};
  gap: ${defaultMargins.xs};
  background-color: ${(p) =>
    p.$automaticDeparture
      ? p.theme.colors.status.warningBackground
      : 'transparent'};
`

const AttendanceTime = styled.span`
  font-weight: ${fontWeights.semibold};
  flex: 1 0 30px;
  text-align: center;
  white-space: nowrap;
`
